import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import dangerIcon from '../../../assets/images/SVG/dangerIcon.svg';
import deleteIcon from '../../../assets/images/SVG/deleteIcon.svg';

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    description = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'danger' // 'danger' | 'warning' | 'info' | 'delete'
}) => {
    // Type-specific icons, colors, and button styles
    const styles = {
        danger: {
            iconBg: 'transparent',
            iconBorder: 'transparent',
            btnBg: '#ef4444',
            btnHoverBg: '#dc2626',
            btnShadow: '0 8px 20px -6px rgba(239, 68, 68, 0.4)',
            btnHoverShadow: '0 12px 24px -6px rgba(239, 68, 68, 0.5)',
            iconColor: 'transparent',
            iconWidth: '80px',
            iconHeight: '80px',
            svg: (
                <Box
                    component="img"
                    src={dangerIcon}
                    alt="Danger"
                    sx={{
                        width: '80px',
                        height: '80px'
                    }}
                />
            )
        },
        delete: {
            iconBg: 'transparent',
            iconBorder: 'transparent',
            btnBg: '#ef4444',
            btnHoverBg: '#dc2626',
            btnShadow: '0 8px 20px -6px rgba(239, 68, 68, 0.4)',
            btnHoverShadow: '0 12px 24px -6px rgba(239, 68, 68, 0.5)',
            iconColor: 'transparent',
            iconWidth: '80px',
            iconHeight: '80px',
            svg: (
                <Box
                    component="img"
                    src={deleteIcon}
                    alt="Delete"
                    sx={{
                        width: '80px',
                        height: '80px'
                    }}
                />
            )
        },
        warning: {
            iconBg: 'rgba(254, 243, 199, 0.7)',
            iconBorder: 'rgba(245, 158, 11, 0.2)',
            btnBg: '#f59e0b',
            btnHoverBg: '#d97706',
            btnShadow: '0 8px 20px -6px rgba(245, 158, 11, 0.4)',
            btnHoverShadow: '0 12px 24px -6px rgba(245, 158, 11, 0.5)',
            iconColor: '#f59e0b',
            svg: (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            )
        },
        info: {
            iconBg: 'rgba(219, 234, 254, 0.7)',
            iconBorder: 'rgba(59, 130, 246, 0.2)',
            btnBg: '#ff502d',
            btnHoverBg: '#e04324',
            btnShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
            btnHoverShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)',
            iconColor: '#3b82f6',
            svg: (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            )
        }
    };

    const currentStyle = styles[type] || styles.info;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            disableScrollLock
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        backdropFilter: 'blur(8px)',
                    }
                }
            }}
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '24px !important',
                    width: '100%',
                    maxWidth: '420px',
                    margin: '16px',
                    boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    fontFamily: "'Poppins', sans-serif",
                    overflow: 'visible',
                    position: 'relative',
                    p: { xs: 2.5, sm: 3.5 },
                    pt: { xs: 4, sm: 5 }
                }
            }}
        >
            {/* Top Right Close Button */}
            <IconButton
                onClick={onClose}
                aria-label="close"
                sx={{
                    position: 'absolute',
                    right: 20,
                    top: 20,
                    color: '#94a3b8',
                    transition: 'all 0.25s',
                    '&:hover': {
                        color: '#64748b',
                        backgroundColor: '#f1f5f9'
                    }
                }}
            >
                <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <DialogContent sx={{ p: 0, textAlign: 'center' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {/* Centered Styled SVG Icon Wrapper */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: currentStyle.iconWidth || '72px',
                            height: currentStyle.iconHeight || '72px',
                            borderRadius: '50%',
                            backgroundColor: currentStyle.iconBg,
                            border: currentStyle.iconBorder === 'transparent' ? 'none' : `1.5px solid ${currentStyle.iconBorder}`,
                            color: currentStyle.iconColor,
                            mb: 3,
                            boxShadow: currentStyle.iconBg === 'transparent' ? 'none' : `0 8px 16px -4px ${currentStyle.iconBg}`,
                        }}
                    >
                        {currentStyle.svg}
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: '#1e1b4b', // deep premium charcoal/indigo
                            mb: 1.5,
                            fontSize: { xs: '19px', sm: '21px' },
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        sx={{
                            color: '#64748b', // soft slate blue
                            fontSize: '13.5px',
                            lineHeight: 1.55,
                            mb: 4,
                            maxWidth: '340px',
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        {description}
                    </Typography>

                    {/* Action Buttons Container */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            width: '100%',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {/* Cancel Button */}
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            sx={{
                                borderColor: '#cbd5e1',
                                color: '#64748b',
                                borderRadius: '16px',
                                width: '100%',
                                py: 1.25,
                                fontSize: '14.5px',
                                fontWeight: 600,
                                textTransform: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    backgroundColor: '#f8fafc',
                                    color: '#475569',
                                    transform: 'translateY(-2px)'
                                },
                                '&:active': {
                                    transform: 'translateY(0)'
                                },
                                transition: 'all 0.25s ease'
                            }}
                        >
                            {cancelText}
                        </Button>

                        {/* Confirm Button */}
                        <Button
                            onClick={onConfirm}
                            variant="contained"
                            sx={{
                                backgroundColor: currentStyle.btnBg,
                                color: '#ffffff',
                                borderRadius: '16px',
                                width: '100%',
                                py: 1.25,
                                fontSize: '14.5px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: currentStyle.btnShadow,
                                fontFamily: "'Poppins', sans-serif",
                                '&:hover': {
                                    backgroundColor: currentStyle.btnHoverBg,
                                    boxShadow: currentStyle.btnHoverShadow,
                                    transform: 'translateY(-2px)'
                                },
                                '&:active': {
                                    transform: 'translateY(0)'
                                },
                                transition: 'all 0.25s ease'
                            }}
                        >
                            {confirmText}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmationModal;
